/**
 * WebDAVManager.js - Manages multiple WebDAV directory instances
 */

import { WebDAVFileSystem } from './webdav/WebDAVFileSystem.js'
import { davTrace } from './webdav/log.js'

export class WebDAVManager {
    constructor() {
        this.webdavInstances = new Map() // pathName -> {filesystem, directoryHandle, displayName}
        this.routeHandlers = new Map() // routePath -> pathName
    }

    /**
     * Add a new WebDAV directory
     */
    async addDirectory(directoryHandle, customName = null, metadata = {}) {
        if (!directoryHandle) {
            throw new Error('Directory handle is required')
        }

        const actualName = directoryHandle.name
        const displayName = customName || actualName

        // Generate a unique path name to avoid conflicts
        let pathName = displayName
        let counter = 1
        while (this.webdavInstances.has(pathName)) {
            pathName = `${displayName}_${counter}`
            counter++
        }

        // Create new WebDAV filesystem instance
        const filesystem = new WebDAVFileSystem()
        filesystem.fileSystemHandler.rootHandle = directoryHandle

        // Store the instance
        this.webdavInstances.set(pathName, {
            filesystem,
            directoryHandle,
            displayName,
            actualName,
            pathName,
            source: metadata.source || 'fsa',
        })

        return pathName
    }

    /**
     * Remove a WebDAV directory by path name
     */
    removeDirectory(pathName) {
        if (this.webdavInstances.has(pathName)) {
            this.webdavInstances.delete(pathName)

            // Remove associated route handlers
            for (const [
                routePath,
                instancePathName,
            ] of this.routeHandlers.entries()) {
                if (instancePathName === pathName) {
                    this.routeHandlers.delete(routePath)
                }
            }

            return true
        }
        return false
    }

    /**
     * Get all WebDAV instances
     */
    getAllInstances() {
        return Array.from(this.webdavInstances.entries()).map(
            ([pathName, instance]) => ({
                pathName,
                ...instance,
            })
        )
    }

    /**
     * Register a route for a specific WebDAV instance
     */
    registerRoute(routePath, pathName) {
        if (this.webdavInstances.has(pathName)) {
            this.routeHandlers.set(routePath, pathName)
            return true
        }
        return false
    }

    /**
     * Unregister a route
     */
    unregisterRoute(routePath) {
        return this.routeHandlers.delete(routePath)
    }

    /**
     * Segment-bounded instance resolution shared by handleRequest and the
     * app's _resolveInstance. The old substring includes() misrouted
     * overlapping directory names ('docs' matched '/docs-archive/…' and
     * '/x/mydocs/…'); a directory name only matches a whole path segment,
     * and the longest registered name wins. Returns
     * { pathName, instance, remainingPath } or null.
     */
    resolveInstance(requestPath) {
        let best = null
        const consider = (rawName, instance) => {
            if (!instance) return
            const pathName = String(rawName).replace(/^\/+|\/+$/g, '')
            if (!pathName) return
            if (best && best.pathName.length >= pathName.length) return
            const needle = `/${pathName}`
            let from = 0
            while (true) {
                const idx = requestPath.indexOf(needle, from)
                if (idx === -1) return
                const end = idx + needle.length
                if (end === requestPath.length || requestPath[end] === '/') {
                    best = {
                        pathName,
                        instance,
                        remainingPath: requestPath.substring(end) || '/',
                    }
                    return
                }
                from = idx + 1
            }
        }
        // Route handlers first: on equal-length names they win the tie.
        for (const pathName of this.routeHandlers.values()) {
            consider(pathName, this.webdavInstances.get(pathName))
        }
        for (const [pathName, instance] of this.webdavInstances.entries()) {
            consider(pathName, instance)
        }
        return best
    }

    /**
     * Handle a WebDAV request by routing to the appropriate instance
     */
    async handleRequest(data) {
        // Extract the path from the request to determine which instance to use
        const requestPath = data.url || data.path || ''
        davTrace(`[routing] WebDAVManager.handleRequest: requestPath=${requestPath}, method=${data.method}, routeHandlers=[${[...this.routeHandlers.keys()].join(', ')}], instances=[${[...this.webdavInstances.keys()].join(', ')}]`)

        // Find the matching WebDAV instance
        let targetInstance = this.resolveInstance(requestPath)?.instance || null

        // If still no match, use the first available instance (fallback)
        if (!targetInstance && this.webdavInstances.size > 0) {
            targetInstance = this.webdavInstances.values().next().value
            davTrace('Using fallback WebDAV instance')
        }

        if (!targetInstance) {
            console.error('No WebDAV instance available for request')
            return {
                status: 503,
                body: 'No WebDAV filesystem available',
            }
        }

        davTrace('Routing request to instance:', targetInstance.pathName)

        try {
            return await targetInstance.filesystem.handleRequest(data)
        } catch (error) {
            console.error('WebDAV request error:', error)
            return {
                status: 500,
                headers: { 'Content-Type': 'text/plain' },
                body: error.message || 'Internal server error',
            }
        }
    }

    /**
     * Check if there are any active WebDAV instances
     */
    hasActiveInstances() {
        return this.webdavInstances.size > 0
    }

    /**
     * Get instance by path name
     */
    getInstance(pathName) {
        return this.webdavInstances.get(pathName)
    }

    /**
     * Update the display name for an instance
     */
    updateDisplayName(pathName, newDisplayName) {
        const instance = this.webdavInstances.get(pathName)
        if (instance) {
            instance.displayName = newDisplayName
            return true
        }
        return false
    }
}
